import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import clx from 'classnames';
import { Megaphone, RefreshCw } from 'lucide-react';
import useWindowSize from '../../utils/useWindowSize';
import { communicationsActions } from './store';
import { getCommunicationToken } from './store/selectors';
import styles from './Communications.module.scss';

const COMMUNICATIONS_BASE = (process.env.REACT_APP_COMMUNICATION || '').replace(/\/+$/, '');

export function Communications() {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const { isMobile } = useWindowSize();
  const communicationToken = useSelector(getCommunicationToken());
  const [frameReady, setFrameReady] = useState(false);
  const [timedOut, setTimedOut] = useState(false);

  const requestToken = () => {
    setFrameReady(false);
    setTimedOut(false);
    dispatch(communicationsActions.GET_TOKEN_COMMUNICATIONS.REQUEST());
  };

  useEffect(() => {
    requestToken();
  }, []);

  useEffect(() => {
    if (communicationToken) {
      setTimedOut(false);
      return undefined;
    }
    const timeoutId = window.setTimeout(() => setTimedOut(true), 12000);
    return () => window.clearTimeout(timeoutId);
  }, [communicationToken]);

  const url = useMemo(() => {
    if (!communicationToken || !COMMUNICATIONS_BASE) return '';
    const language = i18n.language || localStorage?.i18nextLng || 'it';
    return `${COMMUNICATIONS_BASE}/?token=${encodeURIComponent(communicationToken)}&i18nextLng=${encodeURIComponent(language)}`;
  }, [communicationToken, i18n.language]);

  const showFrame = Boolean(url);
  const showSkeleton = showFrame && !frameReady;
  const showError = timedOut && !communicationToken;

  return (
    <div
      className={clx(styles.page, {
        [styles.page_mobile]: isMobile,
      })}
    >
      <div className={styles.frame_card}>
        {showError ? (
          <div className={styles.state} role="alert">
            <span className={styles.state_icon} aria-hidden="true">
              <Megaphone size={22} strokeWidth={2.2} />
            </span>
            <h2 className={styles.state_title}>{t('communications.errorTitle')}</h2>
            <p className={styles.state_copy}>{t('communications.errorDescription')}</p>
            <button type="button" className={styles.retry} onClick={requestToken}>
              <RefreshCw size={16} strokeWidth={2.2} />
              {t('communications.retry')}
            </button>
          </div>
        ) : (
          <>
            {(!showFrame || showSkeleton) && (
              <div className={styles.skeleton} aria-busy="true" aria-label={t('communications.loading')}>
                <div className={styles.skeleton_row}>
                  <div className={styles.skeleton_card} />
                  <div className={styles.skeleton_card} />
                </div>
                <div className={styles.skeleton_table}>
                  <span className={styles.bone} />
                  <span className={styles.bone} />
                  <span className={styles.bone} />
                  <span className={styles.bone} />
                </div>
              </div>
            )}
            {showFrame && (
              <iframe
                title={t('communications.iframeTitle')}
                src={url}
                className={clx(styles.frame, { [styles.frame_hidden]: !frameReady })}
                onLoad={() => setFrameReady(true)}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
}
